/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import { types, applySnapshot } from 'mobx-state-tree';
import Framework from '@greenville/framework';
import _ from 'lodash';
import { toJS } from 'mobx';
import * as constants from '../../../common/constants';

const BookDetail = types.model('BookDetail', {
  _id: types.maybeNull(types.string),
  identifierId: types.maybeNull(types.string),
  bookId: types.maybeNull(types.string), // Keep for backward compatibility with existing data
  title: types.maybeNull(types.string),
  author: types.maybeNull(types.string),
  lifeCycleStatus: types.maybeNull(types.string)
});

const ChatutilityBooks = types
  .model('ChatutilityBooks', {
    data: types.optional(types.array(BookDetail), []),
    status: types.maybeNull(types.string)
  })
  .actions((self) => ({
    fetch(searchParams = '', tenantId = '', status = '') {
      const dataToSend = {
        searchParams,
        tenantId,
        status
      };
      Framework.getEventManager().publish(constants.CHATUTILITY_MANAGE_BOOKS_DATA_FETCHED, dataToSend);
    },
    addBook(addBookData, tenantName) {
      Framework.getEventManager().publish(constants.CHATUTILITY_ADD_BOOK_DATA, { addBookData, tenantName });
    },
    updateBook(data, tenantName) {
      Framework.getEventManager().publish(constants.CHATUTILITY_EDIT_BOOK_DATA, { data, tenantName });
    },
    set(response) {
      const newData = toJS(response.data);
      if (!Array.isArray(newData)) {
        applySnapshot(self.data, []);
        return;
      }
      // Convert bookId from API to identifierId for frontend use
      const normalizedData = newData.map((item) => {
        const normalizedItem = { ...item };
        // API returns bookId, convert to identifierId for frontend
        if (normalizedItem.bookId && !normalizedItem.identifierId) {
          normalizedItem.identifierId = normalizedItem.bookId;
        }
        return normalizedItem;
      });
      applySnapshot(self.data, normalizedData);
    },

    setAddedBookData(addedBookResponse) {
      const updatedState = toJS(self);
      const newItem = toJS(addedBookResponse[0]);
      // Convert bookId from API to identifierId for frontend use
      const normalizedItem = { ...newItem };
      if (normalizedItem.bookId && !normalizedItem.identifierId) {
        normalizedItem.identifierId = normalizedItem.bookId;
      }
      updatedState.data.unshift(normalizedItem);
      applySnapshot(self, updatedState);
      Framework.getEventManager().publish(constants.SET_ADDED_DATA);
    },

    setEditBookData(addedBookResponse) {
      const updatedState = toJS(self);
      const responseData = toJS(addedBookResponse.data);
      // Convert bookId from API to identifierId for frontend use
      const normalizedData = { ...responseData };
      if (normalizedData.bookId && !normalizedData.identifierId) {
        normalizedData.identifierId = normalizedData.bookId;
      }
      const responseIdentifierId = normalizedData.identifierId || normalizedData.bookId;
      updatedState.data = _.map(updatedState.data, (list) => {
        const listIdentifierId = list.identifierId || list.bookId;
        return listIdentifierId === responseIdentifierId ? normalizedData : list;
      });
      applySnapshot(self, updatedState);
      Framework.getEventManager().publish(constants.SET_UPDATED_DATA);
    }
  }));

export default ChatutilityBooks;
